import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';

import { useAlertsStore } from '@tallo/design-system';
import { companyPortfoliosQueryKey } from '@tallo/investor/company';
import { showingAgentsQueryKey } from '@tallo/investor/showing-agents';

import { TeamDto, TeamSubject } from '../dtos/property-team.dto.interface';
import { buildingsQueryKey, getBuildingQueryKey } from '../queries/building.query';
import { accessRequestsToAnswerQueryKey, getPropertyTeamQueryKey } from '../queries/property-team.query';
import { teamServiceFor } from '../services/property-team.service';

/**
 * Every team change answers with the unit's or building's new team, which is shown as is. A seat joining
 * it is also listed as one of its showing agents, and portfolio counts move, so those refresh; the change
 * itself already happened, so a failed refresh is not an error.
 */
function showNewTeam(team: TeamDto, subject: TeamSubject) {
  const queryCache = useQueryCache();

  queryCache.setQueryData(getPropertyTeamQueryKey(subject), team);
  // Adding someone or handing them the unit answers any request they had open
  for (const key of [companyPortfoliosQueryKey, showingAgentsQueryKey, accessRequestsToAnswerQueryKey]) {
    queryCache.invalidateQueries({ key: [key] }).catch(() => undefined);
  }

  // The building's page and cards show its portfolio and manager; exact, or the team just set is read again
  if (subject.kind === 'building') {
    queryCache.invalidateQueries({ key: getBuildingQueryKey(subject.id), exact: true }).catch(() => undefined);
    queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
  }
}

function reportError(error: unknown) {
  useAlertsStore().catchError(error);
}

export const useMovePropertyToPortfolio = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; portfolioId: string | null }) {
      return teamServiceFor(payload.subject).moveToPortfolio(payload.subject.id, payload.portfolioId);
    },
    onSuccess: (team, payload) => showNewTeam(team, payload.subject),
    onError: reportError,
  });
});

export const useHandOffProperty = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; investorId: string; keepPreviousAsCollaborator: boolean }) {
      return teamServiceFor(payload.subject).handOff(
        payload.subject.id,
        payload.investorId,
        payload.keepPreviousAsCollaborator,
      );
    },
    onSuccess: (team, payload) => showNewTeam(team, payload.subject),
    onError: reportError,
  });
});

export const useAddPropertyCollaborator = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; investorId: string }) {
      return teamServiceFor(payload.subject).addCollaborator(payload.subject.id, payload.investorId);
    },
    onSuccess: (team, payload) => showNewTeam(team, payload.subject),
    onError: reportError,
  });
});

export const useRemovePropertyCollaborator = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; investorId: string }) {
      return teamServiceFor(payload.subject).removeCollaborator(payload.subject.id, payload.investorId);
    },
    onSuccess: (team, payload) => showNewTeam(team, payload.subject),
    onError: reportError,
  });
});

/** The caller's own request shows on the team once it is read again. */
export const useRequestPropertyAccess = defineMutation(() => {
  return useMutation({
    mutation: (subject: TeamSubject) => teamServiceFor(subject).requestAccess(subject.id),
    onSettled: (_request, _error, subject) =>
      useQueryCache()
        .invalidateQueries({ key: getPropertyTeamQueryKey(subject) })
        .catch(() => undefined),
    onError: reportError,
  });
});

export const useDeclinePropertyAccessRequest = defineMutation(() => {
  return useMutation({
    mutation(payload: { subject: TeamSubject; requestId: string }) {
      return teamServiceFor(payload.subject).declineAccessRequest(payload.subject.id, payload.requestId);
    },
    onSuccess: (team, payload) => {
      const queryCache = useQueryCache();

      queryCache.setQueryData(getPropertyTeamQueryKey(payload.subject), team);
      queryCache.invalidateQueries({ key: [accessRequestsToAnswerQueryKey] }).catch(() => undefined);
    },
    onError: reportError,
  });
});
